import React, { useState, useEffect, useRef } from "react";
import { Button } from "rsuite";
import { numberFormatter, numberFormatterInK } from "../../utils";
import { CSVLink, CSVDownload } from "react-csv";
import ValueTrackerTable from "./valueTrackerTable";
import { Radio, RadioGroup, Tooltip, Whisper } from "rsuite";
import ValueTrackerChart from "./valueTrackerChart";
import InfoOutlineIcon from "@rsuite/icons/InfoOutline";
import FileDownloadIcon from "@rsuite/icons/FileDownload";

const ValueTracker = (props: any) => {
	const exportFullRef = useRef<any>(null);

	const csvHeaders = [
		{ label: "Supplier", key: "Master_Vendor_Name" },
		{ label: "2024 Spend/Sales", key: "lastYearCost" },
		{ label: "2024 Rebate", key: "last_rebate" },
		{ label: "2025 F Spend/Sales", key: "currFSpend" },
		{ label: "2025 F Rebate", key: "cur_rebate" },
		{ label: "2025 YTD Rebate", key: "Current_Year_YTD_Rebate" },
		{ label: "Incremental Rebate", key: "incrementalRebate" },
	];

	const exportDataClick = () => {
		if (props.export1Data?.length > 0) {
			exportFullRef?.current?.link.click();
		}
	};
	return (
		<>
			<div className="suppliers contentBox flex flex-col justify-start gap-y-[1vh] overflow-x-auto">
				{props?.data?.length > 0 ? (
					<>
						<div className="common-table-header-row !mb-0">
							<div className="common-table-header">
								Suppliers List
							</div>
							<div className="flex items-center">
								<Button
									className="bg-transparent hover:bg-transparent"
									onClick={exportDataClick}
								>
									<FileDownloadIcon
										color="black"
										className="text-fsre15"
									/>
								</Button>

								<CSVLink
									ref={exportFullRef}
									data={props.export1Data}
									headers={csvHeaders}
									filename="nia_negotiations_table.csv"
									className="hidden"
									target="_blank"
								>
									{" "}
								</CSVLink>
							</div>
						</div>
						<ValueTrackerTable
							data={props.data}
							export1Data={props.export1Data}
							updateSelectedSupplier={(data: any) => {
								props.updateSelectedSupplier(data);
							}}
							selectedSupplier={props.selectedSupplier}
						/>
						<div className="text-[#666666] italic textNormal">
							Note: All the Spend / Sales above are adjusted with
							NIA $ & Cash Discounts
						</div>
					</>
				) : (
					<div className="h-full flex justify-center items-center italic font-bold text-[2vh] text-black">
						No Data Available
					</div>
				)}
			</div>

			<div className="detailed-data-container contentBox flex flex-col gap-y-[2vh] overflow-x-auto">
				{props?.tableLoading ? (
					<div className="loaderCustom"></div>
				) : props?.trackerGraphData?.length > 0 ? (
					<div className="flex flex-col h-[100%] gap-x-[2vh]">
						<div className=" text-black font-semibold">
							<RadioGroup
								name="radio-group-inline"
								inline
								className="textNormal"
								defaultValue={props.approach}
								value={props.approach}
								onChange={(value: any) => {
									props.updateApproach(value);
								}}
							>
								<Radio
									value="approach1"
									color="green"
									className="mr-[4vh]"
								>
									Approach 1
									<Whisper
										placement="right"
										speaker={
											<Tooltip className="tooltipconfig">
												<div>
													Prorates the spend by
													annualizing the average
													monthly spend of the
													completed months.
												</div>
											</Tooltip>
										}
									>
										<InfoOutlineIcon className="ml-[.5vh] mb-[.3vh]" />
									</Whisper>
								</Radio>
								<Radio
									value="approach2"
									color="green"
									className="mr-[4vh]"
								>
									Approach 2
									<Whisper
										placement="right"
										speaker={
											<Tooltip className="tooltipconfig">
												<div>
													Assumes the same monthly
													spend for the remaining
													months as observed in the
													corresponding months of the
													previous year.
												</div>
											</Tooltip>
										}
									>
										<InfoOutlineIcon className="ml-[.5vh] mb-[.3vh]" />
									</Whisper>
								</Radio>
								<Radio value="approach3" color="green">
									Approach 3
									<Whisper
										placement="left"
										speaker={
											<Tooltip className="tooltipconfig">
												<div>
													Projects the annual spend by
													applying the growth rate of
													the YTD spend to the average
													spend of the remaining
													months from the previous
													year.
												</div>
											</Tooltip>
										}
									>
										<InfoOutlineIcon className="ml-[.5vh] mb-[.3vh]" />
									</Whisper>
								</Radio>
							</RadioGroup>
						</div>
						<div className="summary flex gap-2 h-[25%]">
							<div className="card">
								<div className="header mb-4 flex items-center">
									Incremental Benefit based on YTD growth
									<Whisper
										placement="right"
										speaker={
											<Tooltip className="tooltipconfig">
												<div>
													Estimates rebate
													enhancements based on the
													growth forecast for current
													year.
												</div>
											</Tooltip>
										}
									>
										<InfoOutlineIcon className=" h-[2rem] w-[2rem]" />
									</Whisper>
								</div>
								<div className="bottom font-bold">
									{numberFormatter(
										props?.keyMetricsData
											?.totalIncrementalBenefit,
										Math.round(
											props?.keyMetricsData
												?.totalIncrementalBenefit
										)?.toString()?.length > 7
											? 2
											: 0,
										"$"
									)}
								</div>
							</div>

							<div className="card">
								<div className="header flex items-center">
									Incremental Benefit based on Method 1
									<Whisper
										placement="right"
										speaker={
											<Tooltip className="tooltipconfig">
												<div>
													Estimates Base Rebate
													improvement assuming 0%
													growth.
												</div>
											</Tooltip>
										}
									>
										<InfoOutlineIcon className=" h-[2rem] w-[2rem]" />
									</Whisper>
								</div>
								<div className="bottom font-bold">
									{numberFormatter(
										props?.keyMetricsData?.lowEndEstimate,
										2,
										"$"
									)}
								</div>
							</div>

							<div className="card">
								<div className="header flex items-center">
									Incremental Benefit based on Method 2
									<Whisper
										placement="right"
										speaker={
											<Tooltip className="tooltipconfig">
												<div>
													Estimates rebate
													enhancements by overlaying
													supplier year-to-date
													performance data onto the
													established growth
													assumption framework.
												</div>
											</Tooltip>
										}
									>
										<InfoOutlineIcon className=" h-[2rem] w-[2rem]" />
									</Whisper>
								</div>
								<div className="bottom font-bold">
									{numberFormatter(
										props?.keyMetricsData?.highEndEstimate,
										2,
										"$"
									)}
								</div>
							</div>

							<div className="card">
								<span className="header">
									Estimated range of Incremental Benefits
								</span>
								<div className="bottom font-bold">
									{props?.keyMetricsData?.lowEndEstimate <
									props?.keyMetricsData?.highEndEstimate
										? `${numberFormatter(
												props?.keyMetricsData
													?.lowEndEstimate,
												2,
												"$"
										  )} - ${numberFormatter(
												props?.keyMetricsData
													?.highEndEstimate,
												2,
												"$"
										  )}`
										: `${numberFormatter(
												props?.keyMetricsData
													?.highEndEstimate,
												2,
												"$"
										  )} - ${numberFormatter(
												props?.keyMetricsData
													?.lowEndEstimate,
												2,
												"$"
										  )}`}
								</div>
							</div>
						</div>
						<div className="flex flex-col h-[90%] w-full pt-[2vh] pb-[0] ">
							<ValueTrackerChart
								chartData={props.trackerGraphData}
								export2Data={props.export2Data}
								spendText={"Spend"}
								approach={props.approach}
								keyMetricsData={props?.keyMetricsData}
							/>
						</div>
					</div>
				) : (
					<div className="h-full flex justify-center items-center italic font-bold text-mainGreen text-[2vh]">
						<h1>No data available</h1>
					</div>
				)}
			</div>
		</>
	);
};

export default ValueTracker;
